import {copilotLocalStorage, type SavedMessageSource} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {useEffect, useRef} from 'react'

// Drafts that flush on leave, including submissions restored before the owner's cleanup runs.
// A replacement can read them before that cleanup writes to storage.
const unsavedDrafts = new Map<string, string>()

/**
 * Saves a composer's text to local storage under `draftId`. Emptying the text
 * removes the saved draft unless unedited `submittedText` is still awaiting settlement.
 * An undefined `draftId` skips saving.
 * `flushOnLeave` saves the latest text immediately when the draft unmounts or `draftId` changes.
 */
export function usePersistedDraft(
  draftId: string | undefined,
  source: SavedMessageSource,
  value: string,
  {flushOnLeave = false, submittedText}: {flushOnLeave?: boolean; submittedText?: string} = {},
) {
  const savedValue = value || submittedText || ''
  const latestRef = useRef({draftId, value: savedValue})

  useEffect(() => {
    latestRef.current = {draftId, value: savedValue}
    if (!draftId) return
    if (flushOnLeave) {
      if (savedValue) unsavedDrafts.set(`${source}:${draftId}`, savedValue)
      else unsavedDrafts.delete(`${source}:${draftId}`)
    }
    if (savedValue) {
      if (submittedText !== undefined) copilotLocalStorage.setSavedMessageFast(draftId, source, savedValue)
      copilotLocalStorage.setSavedMessage(draftId, source, savedValue)
      return
    }

    // `setSavedMessage` waits 100ms and only runs its latest call. Calling it with
    // null cancels a pending save of the old text; the fast clear removes what is already saved.
    // The deferred clear must not remove text written after the fast clear.
    copilotLocalStorage.setSavedMessage(draftId, source, null, null)
    copilotLocalStorage.setSavedMessageFast(draftId, source, null)
  }, [draftId, source, savedValue, submittedText, flushOnLeave])

  useEffect(() => {
    if (!flushOnLeave) return
    return () => {
      // The 100ms wait is shared across all drafts, so the next draft's save would cancel this one's pending save.
      const latest = latestRef.current
      if (latest.draftId !== draftId || !draftId) return
      copilotLocalStorage.setSavedMessageFast(
        draftId,
        source,
        latest.value || unsavedDrafts.get(`${source}:${draftId}`) || null,
      )
      unsavedDrafts.delete(`${source}:${draftId}`)
    }
  }, [draftId, source, flushOnLeave])
}

export function readPersistedDraft(draftId: string | undefined, source: SavedMessageSource): string | null {
  if (!draftId) return null
  return unsavedDrafts.get(`${source}:${draftId}`) ?? copilotLocalStorage.getSavedMessage(draftId, source)
}

/** Removes a saved draft, but only while it still holds `expectedText`, so newer text is kept. */
export function clearPersistedDraft(draftId: string, source: SavedMessageSource, expectedText: string) {
  if (readPersistedDraft(draftId, source) !== expectedText) return
  unsavedDrafts.delete(`${source}:${draftId}`)
  copilotLocalStorage.setSavedMessage(draftId, source, null, null)
  copilotLocalStorage.setSavedMessageFast(draftId, source, null)
}

export function restorePersistedDraft(draftId: string, source: SavedMessageSource, text: string) {
  if (readPersistedDraft(draftId, source)) return
  unsavedDrafts.set(`${source}:${draftId}`, text)
  copilotLocalStorage.setSavedMessageFast(draftId, source, text)
}
